import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const SocialRegistration = () => {
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleRegister = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        setError(error?.message);
        setIsLoading(false);
      }
      // Note: Redirect happens automatically via Supabase OAuth flow
    } catch (err) {
      setError('Failed to connect to Google. Please try again.');
      setIsLoading(false);
    }
  };

  const handleOtherProviders = (providerName) => {
    setError(`${providerName} authentication is not configured yet. Please use email/password registration or Google.`);
  };

  const socialProviders = [
    {
      id: 'google',
      name: 'Google',
      icon: 'Mail',
      color: 'bg-white border-border text-foreground hover:bg-muted',
      description: 'Continue with Google',
      handler: handleGoogleRegister
    },
    {
      id: 'microsoft',
      name: 'Microsoft',
      icon: 'Square',
      color: 'bg-blue-600 text-white hover:bg-blue-700',
      description: 'Continue with Microsoft',
      handler: () => handleOtherProviders('Microsoft')
    },
    {
      id: 'apple',
      name: 'Apple',
      icon: 'Smartphone',
      color: 'bg-black text-white hover:bg-gray-800',
      description: 'Continue with Apple',
      handler: () => handleOtherProviders('Apple')
    }
  ];

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-background text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      {/* Social Provider Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {socialProviders?.map((provider) => (
          <button
            key={provider?.id}
            onClick={provider?.handler}
            disabled={isLoading}
            className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-md border transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${provider?.color}`}
          >
            <Icon name={provider?.icon} size={18} />
            <span className="font-medium text-sm hidden sm:inline">
              {provider?.name}
            </span>
            <span className="font-medium text-sm sm:hidden">
              {provider?.description}
            </span>
          </button>
        ))}
      </div>

      {/* Social Registration Benefits */}
      <div className="bg-muted/50 rounded-lg p-4 mt-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name="Shield" size={16} className="text-success" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground mb-1">
              Secure Social Registration
            </h4>
            <p className="text-xs text-muted-foreground">
              Your social account information is used only for authentication. We don't access your personal data or post on your behalf.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Registration Benefits */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
            <Icon name="Zap" size={14} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Quick Setup</p>
            <p className="text-xs text-muted-foreground">Register in seconds</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-full">
            <Icon name="Lock" size={14} className="text-success" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Secure Login</p>
            <p className="text-xs text-muted-foreground">Protected access</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialRegistration;